/**
 * 採点の結果 (stdio-judge の JudgeReport) を、画面に出す形にする。
 *
 * 隠れたテストの中身は見せない。出すのは、何番目のテストか、どの群か、どの種類で落ちたか (誤答、時間切れ、実行時エラー、
 * 出力が多すぎる) だけ。本番の試験も中身は見せないので、それに合わせる (設計 1-2)。
 *
 * StdioCaseView は、例ごとの札の結果の部品 (PR #3583 の CodingCaseResult の CaseView) と同じ形にしてある。
 * #3583 が入ったら、stdioCaseViews の結果をその部品に渡せば、結果の欄を札の作りに替えられる。
 * それまでは、同じ札の作りを components/coding-test/StdioSubmitResult.tsx が持つ
 */
import type { JudgeReport, TestResult } from "@/lib/stdio-judge/types";

export type StdioCaseView = {
  label: string;
  passed: boolean;
  input: string;
  expected: string;
  actual?: string;
  error?: string;
};

type Judged = Extract<JudgeReport, { status: "judged" }>;

const HIDDEN = "(隠れたテストなので出しません)";

const seconds = (ms: number) => `${ms % 1000 === 0 ? ms / 1000 : (ms / 1000).toFixed(1)} 秒`;

/** 落ちた理由の 1 文。通ったテストは null */
export function failureText(result: TestResult, timeLimitMs: number): string | null {
  switch (result.verdict) {
    case "pass":
      return null;
    case "wrong":
      return "誤答です。出力が期待する出力と違います";
    case "timeout":
      return `時間切れです。${seconds(timeLimitMs)}を越えました`;
    case "error":
      return result.errorLine ? `実行時エラーです。${result.errorLine}` : "実行時エラーで止まりました";
    case "too-much-output":
      return "出力が多すぎます。1MB までです";
    case "skipped":
      return "時間切れが続いたので、試していません";
  }
}

export function testLabel(result: TestResult): string {
  return `テスト ${result.index + 1}${result.group ? `（${result.group}）` : ""}`;
}

export function stdioCaseViews(report: Judged, timeLimitMs: number): StdioCaseView[] {
  return report.results.map((result) => ({
    label: testLabel(result),
    passed: result.verdict === "pass",
    input: HIDDEN,
    expected: HIDDEN,
    ...(failureText(result, timeLimitMs) ? { error: failureText(result, timeLimitMs)! } : {}),
  }));
}

/** 結果の 1 行の補足。最も遅いテストの時間と、時間切れのときの手がかり */
export function judgeNote(report: Judged, timeLimitMs: number): string {
  const parts = [`最も遅いテストは ${report.maxMs} ミリ秒でした (上限は 1 テストあたり ${seconds(timeLimitMs)})`];
  if (report.results.some((r) => r.verdict === "timeout")) {
    parts.push("時間切れは、入力が大きいときに計算が多すぎると出ます。ヒントを見て、同じ計算を何度もしていないか考えてみてください");
  }
  return parts.join("。");
}
